import { useAppSelector } from "@redux/hooks";
import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, Divider } from "@mui/material";
import styles from "./UserDropDown.module.scss";
import {
  selectDashboardData,
  selectProfileData,
} from "@redux/slices/CommonSlice";
import { SignOutIcon } from "customIcons";
import { PAGE_URLS } from "@constants/common";
import ConfirmLogoutModal from "@features/common/modal/confirmLogoutModal/ConfirmLogoutModal";
import { useLogoutUserMutation } from "@features/common/commonApi";

/**
 * @component UserDropDown
 * @description User profile dropdown menu that displays user information, company details,
 * and provides options to switch companies or logout. Appears on hover over the user avatar.
 *
 * @features
 * - Displays user avatar with initials
 * - Shows user profile information (name, email)
 * - Displays company logo and name
 * - Switch company functionality
 * - Logout with confirmation modal
 * - Hover-based dropdown visibility with delay
 * - Click outside to close
 * - Loading state during logout
 *
 * @returns {JSX.Element} User dropdown component with profile menu
 */
const UserDropDown = (): React.ReactElement => {
  /* --------------------------------------------------------------------------
   * Hooks
   * -------------------------------------------------------------------------- */
  const navigate = useNavigate();
  const dashboardData = useAppSelector(selectDashboardData);
  const profileData = useAppSelector(selectProfileData);
  const [logoutUser, { isLoading: isLoggingOut }] = useLogoutUserMutation();

  /* --------------------------------------------------------------------------
   * Local State
   * -------------------------------------------------------------------------- */
  /** Controls dropdown menu visibility */
  const [isOpen, setIsOpen] = useState(false);

  /** Controls logout confirmation modal visibility */
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);

  /** Timeout reference for delayed dropdown close */
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /* --------------------------------------------------------------------------
   * Utility Functions
   * -------------------------------------------------------------------------- */
  /**
   * Generates avatar initials from user's full name
   * @param {string} name - User's full name
   * @returns {object} Avatar props with initials
   * @example "John Doe" -> "JD"
   */
  const generateAvatarInitials = useCallback((name: string) => {
    if (!name) return { children: "A" };

    const nameParts = name.split(" ");
    const firstInitial = nameParts[0]?.[0] || "";

    return {
      children: `${firstInitial}`.toUpperCase(),
    };
  }, []);

  /* --------------------------------------------------------------------------
   * Event Handlers
   * -------------------------------------------------------------------------- */
  /**
   * Shows dropdown menu on mouse enter
   * Clears any pending close timeout
   */
  const handleMouseEnter = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsOpen(true);
  }, []);

  /**
   * Hides dropdown menu with delay on mouse leave
   * 300ms delay allows user to move between avatar and dropdown
   */
  const handleMouseLeave = useCallback(() => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 300);
  }, []);

  /**
   * Navigates to company selection page
   * Closes dropdown after navigation
   */
  const handleSwitchCompany = useCallback(() => {
    navigate(PAGE_URLS.SELECT_CORPORATE);
    setIsOpen(false);
  }, [navigate]);

  /**
   * Opens logout confirmation modal
   */
  const openLogoutModal = useCallback(() => {
    setLogoutModalOpen(true);
  }, []);

  /**
   * Closes logout confirmation modal
   */
  const closeLogoutModal = useCallback(() => {
    setLogoutModalOpen(false);
  }, []);

  /**
   * Handles user logout
   * - Calls logout API
   * - Navigates to login page on success
   * - Logs error on failure
   */
  const handleLogout = useCallback(async () => {
    try {
      await logoutUser().unwrap();
      localStorage.removeItem("selected_corporate");
      localStorage.removeItem("show_corporate_selection");
      navigate(PAGE_URLS.LOGIN);
    } catch (error) {
      console.error("Logout failed:", error);
    }
  }, [logoutUser, navigate]);

  /**
   * Navigates to settings page
   */
  const handleEdit = () => {
    navigate(PAGE_URLS.SETTINGS);
  };

  /* --------------------------------------------------------------------------
   * Render
   * -------------------------------------------------------------------------- */
  return (
    <>
      {/* Dropdown Container - Activates on hover */}
      <div
        className={styles["user-dropdown-container"]}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={(e) => e.stopPropagation()} // Prevent parent click handlers
      >
        {/* User Avatar - Shows initials */}
        <Avatar
          className={styles["profile-avatar"]}
          sx={{
            width: 32,
            height: 32,
            background: "#FFFFFF",
            cursor: "pointer",
            color: "#0e0f0c",
            fontFamily: "Mona Sans",
            fontSize: "14px",
            fontStyle: "normal",
            fontWeight: "600",
            lineHeight: "14px",
            letterSpacing: "0px",
            border: "2px solid rgba(153, 198, 255, .451)",
          }}
          alt="User Avatar"
          {...generateAvatarInitials(dashboardData?.corporateName || "")}
        />

        {/* Dropdown Menu - Shown on hover */}
        {isOpen && (
          <div className={styles["custom-dropdown"]}>
            {/* User & Company Information Section */}
            <div className={styles["dropdown-item__top"]}>
              {/* Company Logo */}
              <div className={styles["dropdown-item__top__left"]}>
                <img
                  src={dashboardData?.logoUrl}
                  alt={`${dashboardData?.corporateName} logo`}
                />
              </div>

              {/* User Details */}
              <div className={styles["dropdown-item__top__right"]}>
                {/* User Name & Edit Icon */}
                <div className={styles["dropdown-item__top__right__top"]}>
                  <h4>{profileData?.full_name}</h4>
                  <button
                    onClick={handleEdit}
                    className={styles["dropdown-item__edit-button"]}
                  >
                    <img src="/images/message-edit.svg" alt="Edit profile" />
                  </button>
                </div>

                {/* User Email */}
                <p>{profileData?.email}</p>

                {/* Switch Company Button */}
                {dashboardData?.showCorporateSwitch && (
                  <div
                    className={styles["dropdown-item__top__right__switch"]}
                    onClick={handleSwitchCompany}
                    role="button"
                    tabIndex={0}
                  >
                    Switch Company
                    <div
                      className={
                        styles["dropdown-item__top__right__switch__icons"]
                      }
                    >
                      <img src="/images/arrow-up.svg" alt="" />
                      <img src="/images/arrow-down.svg" alt="" />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <Divider />

            {/* Sign Out Section */}
            <div
              className={styles["dropdown-item__bottom"]}
              onClick={openLogoutModal}
              role="button"
              tabIndex={0}
            >
              <SignOutIcon />
              <h4>Sign Out</h4>
            </div>
          </div>
        )}
      </div>

      {/* Logout Confirmation Modal */}
      {logoutModalOpen && (
        <ConfirmLogoutModal
          onClose={closeLogoutModal}
          onSubmit={handleLogout}
          isSubmitting={isLoggingOut}
        />
      )}
    </>
  );
};

export default UserDropDown;
